import { formatMinor, parseAmount, todayIso } from '@apka/core'
import type { Cadence, ItemType } from '@apka/core'
import { useState } from 'react'
import type { FormEvent } from 'react'
import { BladApi } from '../dane/api.js'
import { utworzPozycje, zmienPozycje } from '../dane/mutacje.js'
import type { DanePozycji } from '../dane/mutacje.js'
import type { Pozycja } from '../dane/typy.js'
import { Modal } from '../ui/Modal.js'
import { Pole, Wybor } from '../ui/Pole.js'
import { Przycisk } from '../ui/Przycisk.js'
import { useToast } from '../ui/Toast.js'
import { MIESIACE } from '../ui/format.js'
import { uzyjDostawcow, uzyjKategorie, uzyjKlientow } from '../dane/widoki.js'
import './formularz-pozycji.css'

export const WALUTY = ['PLN', 'EUR', 'USD', 'GBP', 'CHF'] as const

interface Props {
  pozycja?: Pozycja
  domyslnaData?: string
  domyslnyRytm?: Cadence
  onZamknij(): void
}

interface Stan {
  type: ItemType
  cadence: Cadence
  title: string
  kwota: string
  currency: string
  categoryId: string
  vendorId: string
  clientId: string
  dayOfMonth: string
  monthOfYear: string
  dueDate: string
  startsOn: string
  endsOn: string
  notes: string
  url: string
}

function stanPoczatkowy(props: Props): Stan {
  const { pozycja } = props
  const dzis = props.domyslnaData ?? todayIso()
  if (pozycja === undefined) {
    return {
      type: 'expense',
      cadence: props.domyslnyRytm ?? (props.domyslnaData === undefined ? 'monthly' : 'one_off'),
      title: '',
      kwota: '',
      currency: 'PLN',
      categoryId: '',
      vendorId: '',
      clientId: '',
      dayOfMonth: String(Number(dzis.slice(8))),
      monthOfYear: String(Number(dzis.slice(5, 7))),
      dueDate: dzis,
      startsOn: dzis,
      endsOn: '',
      notes: '',
      url: '',
    }
  }
  return {
    type: pozycja.type,
    cadence: pozycja.cadence,
    title: pozycja.title,
    kwota: (pozycja.amountMinor / 100).toFixed(2).replace('.', ','),
    currency: pozycja.currency,
    categoryId: pozycja.categoryId ?? '',
    vendorId: pozycja.vendorId ?? '',
    clientId: pozycja.clientId ?? '',
    dayOfMonth: pozycja.dayOfMonth === null ? '1' : String(pozycja.dayOfMonth),
    monthOfYear: pozycja.monthOfYear === null ? '1' : String(pozycja.monthOfYear),
    dueDate: pozycja.dueDate ?? dzis,
    startsOn: pozycja.startsOn,
    endsOn: pozycja.endsOn ?? '',
    notes: pozycja.notes ?? '',
    url: pozycja.url ?? '',
  }
}

/** Zamienia stan formularza na ładunek dla API; zwraca komunikat, gdy dane nie mają sensu. */
function zbuduj(stan: Stan): DanePozycji | string {
  const tytul = stan.title.trim()
  if (tytul === '') return 'Podaj tytuł pozycji'

  let amountMinor: number
  try {
    amountMinor = parseAmount(stan.kwota)
  } catch {
    return 'Kwota musi być liczbą, np. 12,90'
  }
  if (amountMinor <= 0) return 'Kwota musi być większa od zera'

  return {
    type: stan.type,
    cadence: stan.cadence,
    title: tytul,
    amountMinor,
    currency: stan.currency,
    vatRate: null,
    categoryId: stan.categoryId === '' ? null : stan.categoryId,
    vendorId: stan.vendorId === '' ? null : stan.vendorId,
    clientId: stan.clientId === '' ? null : stan.clientId,
    dayOfMonth: stan.cadence === 'one_off' ? null : Number(stan.dayOfMonth),
    monthOfYear: stan.cadence === 'yearly' ? Number(stan.monthOfYear) : null,
    dueDate: stan.cadence === 'one_off' ? stan.dueDate : null,
    startsOn: stan.cadence === 'one_off' ? stan.dueDate : stan.startsOn,
    endsOn: stan.endsOn === '' ? null : stan.endsOn,
    notes: stan.notes.trim() === '' ? null : stan.notes.trim(),
    url: stan.url.trim() === '' ? null : stan.url.trim(),
  }
}

/**
 * Zapis do bazy lokalnej normalnie trwa milisekundy. Jeśli przeciąga się
 * ponad sekundy, to znaczy, że coś go blokuje — najczęściej starsza karta
 * trzymająca poprzedni schemat. Bez tego limitu obietnica nigdy by nie wróciła
 * i przycisk zostałby wciśnięty w nieskończoność, bez śladu przyczyny.
 */
const LIMIT_ZAPISU_MS = 6000

function zLimitem<T>(zadanie: Promise<T>): Promise<T> {
  return Promise.race([
    zadanie,
    new Promise<never>((_, odrzuc) =>
      setTimeout(
        () =>
          odrzuc(
            new Error(
              'Zapis się nie kończy. Zamknij inne karty z aplikacją i odśwież tę stronę.',
            ),
          ),
        LIMIT_ZAPISU_MS,
      ),
    ),
  ])
}

function podgladKwoty(stan: Stan): string | null {
  try {
    return formatMinor(parseAmount(stan.kwota), stan.currency)
  } catch {
    return null
  }
}

export function FormularzPozycji(props: Props) {
  const { pozycja, onZamknij } = props
  const [stan, ustawStan] = useState<Stan>(() => stanPoczatkowy(props))
  const [blad, ustawBlad] = useState<string | null>(null)
  const [pracuje, ustawPracuje] = useState(false)
  const toast = useToast()

  const kategorie = uzyjKategorie() ?? []
  const dostawcy = uzyjDostawcow() ?? []
  const klienci = uzyjKlientow() ?? []

  const zmien = <K extends keyof Stan>(klucz: K, wartosc: Stan[K]) => {
    ustawStan((poprzedni) => ({ ...poprzedni, [klucz]: wartosc }))
  }

  async function wyslij(zdarzenie: FormEvent) {
    zdarzenie.preventDefault()
    const dane = zbuduj(stan)
    if (typeof dane === 'string') {
      ustawBlad(dane)
      return
    }

    ustawBlad(null)
    ustawPracuje(true)
    try {
      if (pozycja === undefined) {
        await zLimitem(utworzPozycje(dane))
        toast.pokaz('Dodano pozycję')
      } else {
        await zLimitem(zmienPozycje(pozycja.id, dane))
        toast.pokaz('Zapisano zmiany')
      }
      onZamknij()
    } catch (problem) {
      // Komunikat z limitu czasu niesie konkretną radę, więc nie zastępujemy
      // go ogólnikiem — użytkownik ma wiedzieć, co zrobić.
      ustawBlad(problem instanceof Error ? problem.message : 'Nie udało się zapisać')
    } finally {
      ustawPracuje(false)
    }
  }

  const podglad = podgladKwoty(stan)

  return (
    <Modal
      tytul={pozycja === undefined ? 'Nowa pozycja' : 'Edycja pozycji'}
      onZamknij={onZamknij}
      stopka={
        <>
          <Przycisk wariant="cichy" onClick={onZamknij}>
            Anuluj
          </Przycisk>
          <Przycisk wariant="glowny" pracuje={pracuje} form="formularz-pozycji" type="submit">
            {pozycja === undefined ? 'Dodaj' : 'Zapisz'}
          </Przycisk>
        </>
      }
    >
      <form id="formularz-pozycji" className="formularz" onSubmit={wyslij}>
        <div className="przelaczniki" role="group" aria-label="Rodzaj pozycji">
          {(
            [
              ['expense', 'Koszt'],
              ['income', 'Przychód'],
            ] as const
          ).map(([wartosc, etykieta]) => (
            <button
              key={wartosc}
              type="button"
              className={stan.type === wartosc ? 'pigulka pigulka--wybrana' : 'pigulka'}
              aria-pressed={stan.type === wartosc}
              onClick={() => zmien('type', wartosc)}
            >
              {etykieta}
            </button>
          ))}
        </div>

        <div className="przelaczniki" role="group" aria-label="Rytm">
          {(
            [
              ['monthly', 'Miesięczny'],
              ['yearly', 'Roczny'],
              ['one_off', 'Jednorazowy'],
            ] as const
          ).map(([wartosc, etykieta]) => (
            <button
              key={wartosc}
              type="button"
              className={stan.cadence === wartosc ? 'pigulka pigulka--wybrana' : 'pigulka'}
              aria-pressed={stan.cadence === wartosc}
              onClick={() => zmien('cadence', wartosc)}
            >
              {etykieta}
            </button>
          ))}
        </div>

        <Pole
          etykieta="Tytuł"
          value={stan.title}
          onChange={(zdarzenie) => zmien('title', zdarzenie.target.value)}
          placeholder="np. Hetzner CX32"
          autoFocus
        />

        <div className="siatka-pol siatka-pol--dwie">
          <Pole
            etykieta="Kwota"
            inputMode="decimal"
            value={stan.kwota}
            onChange={(zdarzenie) => zmien('kwota', zdarzenie.target.value)}
            placeholder="12,90"
            podpowiedz={podglad === null ? 'Przecinek lub kropka' : podglad}
          />
          <Wybor
            etykieta="Waluta"
            value={stan.currency}
            onChange={(zdarzenie) => zmien('currency', zdarzenie.target.value)}
          >
            {WALUTY.map((kod) => (
              <option key={kod} value={kod}>
                {kod}
              </option>
            ))}
          </Wybor>
        </div>

        {stan.cadence === 'one_off' ? (
          <Pole
            etykieta="Data płatności"
            type="date"
            value={stan.dueDate}
            onChange={(zdarzenie) => zmien('dueDate', zdarzenie.target.value)}
          />
        ) : (
          <div className="siatka-pol siatka-pol--dwie">
            {stan.cadence === 'yearly' ? (
              <Wybor
                etykieta="Miesiąc"
                value={stan.monthOfYear}
                onChange={(zdarzenie) => zmien('monthOfYear', zdarzenie.target.value)}
              >
                {MIESIACE.map((nazwa, indeks) => (
                  <option key={nazwa} value={indeks + 1}>
                    {nazwa}
                  </option>
                ))}
              </Wybor>
            ) : null}
            <Wybor
              etykieta="Dzień miesiąca"
              value={stan.dayOfMonth}
              onChange={(zdarzenie) => zmien('dayOfMonth', zdarzenie.target.value)}
              podpowiedz="Krótsze miesiące skracają termin do ostatniego dnia"
            >
              {Array.from({ length: 31 }, (_, indeks) => (
                <option key={indeks + 1} value={indeks + 1}>
                  {indeks + 1}
                </option>
              ))}
            </Wybor>
          </div>
        )}

        {stan.cadence === 'one_off' ? null : (
          <div className="siatka-pol siatka-pol--dwie">
            <Pole
              etykieta="Obowiązuje od"
              type="date"
              value={stan.startsOn}
              onChange={(zdarzenie) => zmien('startsOn', zdarzenie.target.value)}
            />
            <Pole
              etykieta="Obowiązuje do"
              type="date"
              value={stan.endsOn}
              onChange={(zdarzenie) => zmien('endsOn', zdarzenie.target.value)}
              podpowiedz="Puste = bezterminowo"
            />
          </div>
        )}

        <div className="siatka-pol siatka-pol--dwie">
          <Wybor
            etykieta="Kategoria"
            value={stan.categoryId}
            onChange={(zdarzenie) => zmien('categoryId', zdarzenie.target.value)}
          >
            <option value="">Bez kategorii</option>
            {kategorie.map((kategoria) => (
              <option key={kategoria.id} value={kategoria.id}>
                {kategoria.name}
              </option>
            ))}
          </Wybor>
          <Wybor
            etykieta="Dostawca"
            value={stan.vendorId}
            onChange={(zdarzenie) => zmien('vendorId', zdarzenie.target.value)}
          >
            <option value="">Bez dostawcy</option>
            {dostawcy.map((dostawca) => (
              <option key={dostawca.id} value={dostawca.id}>
                {dostawca.name}
              </option>
            ))}
          </Wybor>
        </div>

        {klienci.length > 0 ? (
          <Wybor
            etykieta="Klient"
            value={stan.clientId}
            onChange={(zdarzenie) => zmien('clientId', zdarzenie.target.value)}
            podpowiedz="Przypisz koszt do klienta, jeśli go dla niego ponosisz"
          >
            <option value="">Koszt własny</option>
            {klienci.map((klient) => (
              <option key={klient.id} value={klient.id}>
                {klient.name}
              </option>
            ))}
          </Wybor>
        ) : null}

        <Pole
          etykieta="Notatka"
          value={stan.notes}
          onChange={(zdarzenie) => zmien('notes', zdarzenie.target.value)}
          placeholder="Opcjonalnie"
        />

        {blad !== null ? (
          <p className="formularz__blad" role="alert">
            {blad}
          </p>
        ) : null}
      </form>
    </Modal>
  )
}
